import { useState, useEffect } from 'react';
import { ProfileData, Message, User } from '../types';
import { useNotification } from '../../../hooks/useNotification';

export const useProfileForm = (user: User | null, updateProfile: (data: ProfileData) => Promise<any>) => {
  const [isEditing, setIsEditing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<Message>({ type: '', content: '' });
  const { success, error } = useNotification();
  
  const [profileData, setProfileData] = useState<ProfileData>({
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    faculty_and_major: '',
    bio: '',
    gender: '',
    language: '',
    phone: '',
    profile_picture: '',
    positions: '',
  });

  const [originalData, setOriginalData] = useState<ProfileData>(profileData);

  // Initialize form data when user loads
  useEffect(() => {
    if (user) {
      const userData: ProfileData = {
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        organization: user.userprofile?.organization || '',
        faculty_and_major: user.userprofile?.faculty_and_major || '',
        bio: user.userprofile?.bio || '',
        gender: user.userprofile?.gender || '',
        language: user.userprofile?.language || '',
        phone: user.userprofile?.phone || '',
        profile_picture: user.userprofile?.profile_picture || '',
        positions: user.userprofile?.positions || '', 
      };
      setProfileData(userData);
      setOriginalData(userData);
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!user) return;
    
    setLoading(true);
    setMessage({ type: '', content: '' });

    try {
      const result = await updateProfile(profileData);

      if (result.success) {
        success('Profile updated successfully!');
        setOriginalData(profileData);
      } else {
        const errorMessage = result.errors?.general || 
                           (result.errors && Object.values(result.errors)[0]) || 
                           'Failed to update profile';
        error(errorMessage);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        content: (error as Error).message || 'An unexpected error occurred' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProfileData(originalData);
    setMessage({ type: '', content: '' });
  };

  const hasChanges = JSON.stringify(profileData) !== JSON.stringify(originalData);

  return {
    isEditing,
    setIsEditing,
    loading,
    message,
    setMessage,
    profileData,
    originalData,
    hasChanges,
    isSaved: !hasChanges,   // ← 不再是独立状态，直接从 hasChanges 推导
    handleInputChange,
    handleSubmit,
    handleCancel
  };
};